import {useState, useEffect, useMemo} from 'react'
import {toast} from 'react-toastify'
import {
  Text,
  ModalBody,
  Flex,
  Heading,
  Divider,
  Box,
  Tooltip,
  Icon,
  Spinner,
} from '@chakra-ui/react'
import {PropTypes} from 'prop-types'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import {useLocation} from 'react-router'
import {CopyIcon, DownloadIcon, CheckIcon} from '@chakra-ui/icons'
import {useTranslation} from 'react-i18next'

import {
  AttachmentPill,
  EmailPill,
  Header,
  ButtonWithIcon,
  Editor,
  HoveringContinue,
} from '../components'
import {PaperClipIcon, PdfIcon, SendIcon} from '../../../icons'
import {useRails} from '../../../contexts/rails'
import {fetcher, defaultHeaders} from '../../../../api/fetcher'
import {
  fetchDownload,
  triggerBrowserDownload,
} from '../../../../utils/downloadUtils'
import ToastMessage from '../../Toast'

const CustomizeEmailTemplateModal = ({goToPreviousStep, goToNextStep}) => {
  const {t} = useTranslation('invitePayers')
  const location = useLocation()
  const {
    organisation: {currentSupplier},
    authenticityToken,
  } = useRails()

  const supplierId = currentSupplier?.id
  const {
    data: transitionData,
    isLoading: transitionLoading,
    mutate: mutateTransitionData,
  } = useSWR(
    `/api/suppliers/${supplierId}/banking_details_transitions`,
    fetcher,
  )
  const {data: emailTemplateData, isLoading: emailTemplateLoading} = useSWR(
    `/api/suppliers/${supplierId}/banking_details_transitions/fetch_email_template`,
    fetcher,
  )

  const [editorContent, setEditorContent] = useState(
    emailTemplateData?.data || '',
  )
  const [showAllEmails, setShowAllEmails] = useState(false)
  const [downloadedAttachmentsCount, setDownloadedAttachmentsCount] =
    useState(0)
  const [hasInitializedGreeting, setHasInitializedGreeting] = useState(false)
  // Button presses
  const [customerEmailListCopied, setCustomerEmailListCopied] = useState(false)
  const [customerEmailListCopyPerformed, setCustomerEmailListCopyPerformed] =
    useState(false)
  const [emailContentCopied, setEmailContentCopied] = useState(false)
  const [emailContentCopyPerformed, setEmailContentCopyPerformed] =
    useState(false)
  const [downloadAll, setDownloadAll] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  // Selected customer tracking
  const [selectedCustomers, setSelectedCustomers] = useState(
    location.state?.selectedCustomers || [],
  )
  const customerIds = selectedCustomers.map((customer) => customer.id)
  const marmaladeEmail = 'banking-details-no-reply@withmarmalade.com'

  const {trigger: recipientTrigger} = useSWRMutation(
    `/api/suppliers/${supplierId}/banking_details_transitions/add_recipients`,
    async (url) => {
      return fetcher(url, {
        method: 'PUT',
        headers: defaultHeaders({authenticityToken}),
        body: JSON.stringify({customer_ids: customerIds}),
      })
    },
  )

  const {trigger: emailTemplateTrigger} = useSWRMutation(
    `/api/suppliers/${supplierId}/banking_details_transitions/verify_email_contents`,
    async (url) => {
      return fetcher(url, {
        method: 'PUT',
        headers: defaultHeaders({authenticityToken}),
        body: JSON.stringify({email_content: editorContent}),
      })
    },
  )

  const handleCopyEmails = async () => {
    const customerEmails = selectedCustomers
      .map((customer) => customer.email)
      .join(', ')
    const emails = `${marmaladeEmail}, ${customerEmails}`
    navigator.clipboard.writeText(emails)
    setCustomerEmailListCopied(true)

    try {
      await recipientTrigger()
      setCustomerEmailListCopyPerformed(true)
    } catch (error) {
      setCustomerEmailListCopyPerformed(false)
      toast.error(<ToastMessage>{error.message}</ToastMessage>)
    }
  }

  const handleCopyEmailTemplate = async () => {
    const htmlBlob = new Blob([editorContent], {type: 'text/html'})
    const textBlob = new Blob([editorContent.replace(/<[^>]*>/g, '')], {
      type: 'text/plain',
    })

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      }),
    ])

    setEmailContentCopied(true)

    try {
      await emailTemplateTrigger()
      setEmailContentCopyPerformed(true)
    } catch (error) {
      // When the backend returns an array, it gets stringified as comma-separated values
      // e.g., "email_content_bsb,email_content_number"
      const errorKeys =
        typeof error.message === 'string' && error.message.includes(',')
          ? error.message.split(',')
          : [error.message]

      const errorMessages = errorKeys.map((errorKey) => {
        const trimmedKey = errorKey.trim()
        // Check if this looks like an error key (not a full sentence)
        if (trimmedKey && !trimmedKey.includes(' ')) {
          return {
            key: trimmedKey,
            message: t(
              `modal.steps.customizeEmailTemplate.validateTemplate.errors.${trimmedKey}`,
            ),
          }
        }
        return {key: trimmedKey, message: trimmedKey}
      })

      toast.error(
        <ToastMessage>
          {errorMessages.map(({key, message}) => (
            <div key={key}>{message}</div>
          ))}
        </ToastMessage>,
      )
    }
  }

  const handleRemoveCustomer = (customerId) => {
    setSelectedCustomers((prevCustomers) =>
      prevCustomers.filter((c) => c.id !== customerId),
    )
  }

  const handleGoToPreviousStep = () => {
    goToPreviousStep({selectedCustomers})
  }

  const handleContinue = async () => {
    if (!transitionLoading) {
      const errors = []
      if (!customerEmailListCopyPerformed)
        errors.push(
          t('modal.steps.customizeEmailTemplate.errors.recipientsNotCopied'),
        )
      if (!downloadedAttachmentsCount)
        errors.push(
          t(
            'modal.steps.customizeEmailTemplate.errors.attachmentsNotDownloaded',
          ),
        )
      if (!emailContentCopyPerformed)
        errors.push(
          t('modal.steps.customizeEmailTemplate.errors.emailTemplateNotCopied'),
        )

      if (errors.length) {
        toast.error(<ToastMessage>{errors.join(' ')}</ToastMessage>)
      } else {
        goToNextStep('confirmation_page', {selectedCustomers})
      }
    }
  }

  const downloadButtonText = useMemo(() => {
    if (isDownloading) {
      return t('modal.steps.customizeEmailTemplate.buttons.downloading')
    }
    if (downloadAll) {
      return t('modal.steps.customizeEmailTemplate.buttons.downloaded')
    }
    return t('modal.steps.customizeEmailTemplate.buttons.download')
  }, [isDownloading, downloadAll, t])

  const downloadButtonIcon = useMemo(() => {
    if (isDownloading) {
      return <Spinner size="sm" />
    }
    if (downloadAll) {
      return <CheckIcon color="green.500" />
    }
    return <DownloadIcon />
  }, [isDownloading, downloadAll])

  const handleDownloadAll = async () => {
    setIsDownloading(true)
    try {
      const {blob, filename} = await fetchDownload(
        `/api/suppliers/${supplierId}/banking_details_transitions/download_required_assets`,
      )
      triggerBrowserDownload(blob, filename)
      setDownloadAll(true)
      await mutateTransitionData()
    } catch {
      toast.error(
        <ToastMessage>
          {t('modal.steps.customizeEmailTemplate.errors.downloadFailed')}
        </ToastMessage>,
      )
    } finally {
      setIsDownloading(false)
    }
  }

  useEffect(() => {
    setEmailContentCopyPerformed(false)
  }, [editorContent])

  useEffect(() => {
    if (!emailTemplateLoading && emailTemplateData && !hasInitializedGreeting) {
      const templateContent = emailTemplateData.data || ''
      let greeting = ''

      if (selectedCustomers.length === 1) {
        const customerName =
          selectedCustomers[0].contactName || selectedCustomers[0].name
        greeting = `<p>${t(
          'modal.steps.customizeEmailTemplate.body.greeting.single',
          {name: customerName},
        )}</p>`
      } else if (selectedCustomers.length > 1) {
        greeting = `<p>${t(
          'modal.steps.customizeEmailTemplate.body.greeting.multiple',
        )}</p>`
      }

      const contentWithGreeting = greeting
        ? `${greeting}${templateContent}`
        : templateContent
      setEditorContent(contentWithGreeting)
      setHasInitializedGreeting(true)
    }
  }, [
    emailTemplateLoading,
    emailTemplateData,
    selectedCustomers,
    hasInitializedGreeting,
    t,
  ])

  // Dynamically update downloaded attachments count
  useEffect(() => {
    if (
      transitionData?.data.bank_letter_downloaded_at &&
      transitionData?.data.bank_details_pdf_downloaded_at
    ) {
      setDownloadedAttachmentsCount(2)
    } else if (
      transitionData?.data.bank_letter_downloaded_at ||
      transitionData?.data.bank_details_pdf_downloaded_at
    ) {
      setDownloadedAttachmentsCount(1)
    } else {
      setDownloadedAttachmentsCount(0)
    }
  }, [transitionData])

  // Refresh copy button icons based on time
  useEffect(() => {
    if (customerEmailListCopied) {
      const timer = setTimeout(() => {
        setCustomerEmailListCopied(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [customerEmailListCopied])

  useEffect(() => {
    if (downloadAll) {
      const timer = setTimeout(() => {
        setDownloadAll(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [downloadAll])

  useEffect(() => {
    if (emailContentCopied) {
      const timer = setTimeout(() => {
        setEmailContentCopied(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [emailContentCopied])

  const displayedCustomers =
    showAllEmails || selectedCustomers.length <= 2
      ? selectedCustomers
      : selectedCustomers.slice(0, 2)
  const remainingCount = selectedCustomers.length - 2

  return (
    <>
      <Header
        t={t}
        goToPreviousStep={handleGoToPreviousStep}
        goToPreviousStepEnabled
      />
      <ModalBody
        py="xs"
        overflowY="auto"
        overflowX="hidden"
        maxH="calc(100vh - 200px)"
      >
        <Text
          pb={2}
          textTransform="uppercase"
          fontSize="sm"
          lineHeight="110%"
          fontWeight="bold"
        >
          {t('modal.steps.customizeEmailTemplate.stepIndicator')}
        </Text>
        {/* Main title */}
        <Flex direction="column" mb={6}>
          <Heading
            fontSize="xl"
            fontWeight="normal"
            lineHeight="120%"
            color="black"
            mb={2}
          >
            {t('modal.steps.customizeEmailTemplate.title')}
          </Heading>
          <Text fontSize="sm" fontWeight="normal" lineHeight="125%">
            {t('modal.steps.customizeEmailTemplate.subtitle')}
          </Text>
        </Flex>

        <Divider borderColor="black" borderWidth="1px" my={4} />

        {/* List of customers */}
        {selectedCustomers.length > 0 && (
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Flex
              gap={2}
              alignItems="flex-start"
              flexWrap="wrap"
              flex="1"
              mr={4}
            >
              <Text fontWeight="semibold" whiteSpace="nowrap">
                {t(
                  'modal.steps.customizeEmailTemplate.selectedCustomers.label',
                )}
              </Text>
              <Tooltip
                label={t(
                  'modal.steps.customizeEmailTemplate.marmaladeEmailTooltip',
                )}
                placement="bottom"
              >
                <span>
                  <EmailPill
                    key="marmalade"
                    email={marmaladeEmail}
                    bg="neutral.300"
                    fontColor="mld.neutral.700"
                  />
                </span>
              </Tooltip>
              {displayedCustomers.map((customer) => (
                <EmailPill
                  key={customer.id}
                  email={customer.email}
                  onRemove={
                    selectedCustomers.length > 1
                      ? () => handleRemoveCustomer(customer.id)
                      : null
                  }
                />
              ))}
              {!showAllEmails && remainingCount > 0 && (
                <Box
                  display="inline-flex"
                  alignItems="center"
                  bg="neutral.200"
                  border="1px solid"
                  borderColor="neutral.600"
                  borderRadius="md"
                  px={1}
                  cursor="pointer"
                  onClick={() => setShowAllEmails(true)}
                  _hover={{bg: 'neutral.300'}}
                >
                  <Text
                    fontSize="16px"
                    fontWeight="regular"
                    color="mld.primary.100"
                    lineHeight="150%"
                  >
                    {t(
                      'modal.steps.customizeEmailTemplate.selectedCustomers.ommittedPayers',
                      {count: remainingCount},
                    )}
                  </Text>
                </Box>
              )}
            </Flex>
            <ButtonWithIcon
              variant="outline"
              className="fs-unmask"
              text={
                customerEmailListCopied
                  ? t('modal.steps.customizeEmailTemplate.buttons.copied')
                  : t('modal.steps.customizeEmailTemplate.buttons.copy')
              }
              icon={
                customerEmailListCopied ? (
                  <CheckIcon color="green.500" />
                ) : (
                  <CopyIcon />
                )
              }
              onClick={handleCopyEmails}
            />
          </Flex>
        )}

        <Divider borderColor="black" my={4} />

        {/* Attachment Section */}
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            <Text fontWeight="semibold">
              {t('modal.steps.customizeEmailTemplate.attachment.title')}
            </Text>
            <Flex color="neutral.700" px={2} gap={1} alignItems="center">
              <PaperClipIcon transform="rotate(-90deg)" />
              <Text>
                {t('modal.steps.customizeEmailTemplate.attachment.count', {
                  count: 2,
                })}
              </Text>
            </Flex>
            <Flex gap={3}>
              <AttachmentPill
                icon={<PdfIcon />}
                text={t(
                  'modal.steps.customizeEmailTemplate.attachment.bankVerification',
                )}
                attachmentDownloadPath={`/api/suppliers/${supplierId}/banking_details_transitions/download_bank_verification`}
              />
              <AttachmentPill
                icon={<PdfIcon />}
                text={t(
                  'modal.steps.customizeEmailTemplate.attachment.bankStatement',
                  {supplier_name: currentSupplier?.name},
                )}
                attachmentDownloadPath={`/api/suppliers/${supplierId}/banking_details_transitions/download_supplier_bank_statement`}
              />
            </Flex>
          </Flex>
          <ButtonWithIcon
            variant="outline"
            className="fs-unmask"
            text={downloadButtonText}
            icon={downloadButtonIcon}
            onClick={handleDownloadAll}
            disabled={isDownloading}
          />
        </Flex>

        <Divider borderColor="black" my={4} />

        <Flex flexDirection="column" pb="60px">
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontWeight="semibold">
              {t('modal.steps.customizeEmailTemplate.body.title')}
            </Text>
            <ButtonWithIcon
              variant="outline"
              className="fs-unmask"
              text={
                emailContentCopied
                  ? t('modal.steps.customizeEmailTemplate.buttons.copied')
                  : t('modal.steps.customizeEmailTemplate.buttons.copy')
              }
              icon={
                emailContentCopied ? (
                  <CheckIcon color="green.500" />
                ) : (
                  <CopyIcon />
                )
              }
              onClick={handleCopyEmailTemplate}
            />
          </Flex>
          <Box my={4} className="fs-mask">
            <Editor value={editorContent} setValue={setEditorContent} />
          </Box>
        </Flex>
        <HoveringContinue onClick={handleContinue} t={t}>
          <Flex alignItems="center" gap={4}>
            <Flex alignItems="center" gap={2}>
              <Icon as={SendIcon} boxSize="6" />
              <Text fontSize="sm" color="neutral.700">
                {t(
                  'modal.steps.customizeEmailTemplate.hoveringContinue.recipient',
                  {
                    count: selectedCustomers.length,
                  },
                )}
              </Text>
            </Flex>
            <Divider
              borderColor="black"
              orientation="vertical"
              borderLeftWidth="2px"
              height="40px"
            />
            <Flex alignItems="center" gap={2}>
              <Icon as={PaperClipIcon} boxSize="6" transform="rotate(-90deg)" />
              <Text fontSize="sm" color="neutral.700">
                {t(
                  'modal.steps.customizeEmailTemplate.hoveringContinue.downloadedAttachments',
                  {
                    count: downloadedAttachmentsCount,
                  },
                )}
              </Text>
            </Flex>
          </Flex>
        </HoveringContinue>
      </ModalBody>
    </>
  )
}

CustomizeEmailTemplateModal.propTypes = {
  goToPreviousStep: PropTypes.func.isRequired,
  goToNextStep: PropTypes.func.isRequired,
}

export default CustomizeEmailTemplateModal
